import { Link } from "react-router-dom";
import { ArrowLeft, Phone, MessageCircle, MapPin, Bike, RefreshCw, Store, Star, X, Loader2, Undo2 } from "lucide-react";
import { inr2 } from "../../lib/api";
import { FullPageLoader } from "../../components/Loaders";
import ReviewModal from "../../components/ReviewModal";
import { formatDeliveryEta } from "../../lib/deliveryEta";
import CallButton from "../../components/CallButton";
import { useIsBlinkit } from "../../lib/storefrontTheme";
import { deriveOrderView, useOrderTrackController } from "./controllers/useOrderTrackController";
import LseOrderExtras from "../../components/lse/LseOrderExtras";
import OrderTrackMap from "./_partials/OrderTrackMap";
import BkOrderDetailView from "./blinkit/BkOrderDetailView";
import { OrderGroupChip } from "../../components/MultiShop";

export default function OrderTrackPage() {
    return useIsBlinkit() ? <BkOrderDetailView /> : <ClassicOrderTrackPage />;
}

function ClassicOrderTrackPage() {
    const {
        id, tenant, data, track, refreshing, showReview, setShowReview, existingReview, cancelling, returnRequest,
        showReturnModal, setShowReturnModal, returnReason, setReturnReason, submittingReturn, loadTrack,
        loadReviewState, submitReturnRequest, cancelOrder, points,
    } = useOrderTrackController();

    if (!data) return <FullPageLoader />;
    const {
        o, events, status, returnWindowDays, returnWindowOpen, reviewBlockedByReturn, stepLabel, stepColor, riderStatus,
        hasMap,
    } = deriveOrderView({ data, track, tenant, returnRequest, points });

    return (
        <div className="pb-12">
            <header className="px-4 pt-5 pb-4 flex items-center gap-3 bg-card sticky top-0 z-30 border-b border-border">
                <Link to="/orders" className="p-1 -ml-1" data-testid="track-back"><ArrowLeft size={22} /></Link>
                <div className="flex-1">
                    <h1 className="text-base font-bold" data-testid="order-id">{o.order_unique_id}</h1>
                    <p className="text-xs text-muted-foreground">{o.restaurant_name}</p>
                    {/* Only on an order that came from a multi-shop checkout: the way back to its sibling orders. */}
                    <OrderGroupChip order={o} link className="mt-1" />
                </div>
                <button
                    onClick={loadTrack}
                    disabled={refreshing}
                    className="text-xs font-semibold inline-flex items-center gap-1.5 rounded-lg bg-muted hover:bg-muted/80 px-3 py-1.5 disabled:opacity-60"
                    data-testid="track-refresh"
                ><RefreshCw size={13} className={refreshing ? "animate-spin" : ""} /> Refresh</button>
            </header>

            {/* iter178 — desktop (lg+): two columns, same convention as Cart / Checkout. Below lg both wrappers are plain
                blocks, so phones and tablets render exactly as before. */}
            <div className="lg:grid lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] lg:items-start">
            <div className="min-w-0">
            <section className="m-4 bg-card rounded-2xl border border-border p-5" data-testid="track-status-card">
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Status</p>
                <h2
                    className="text-2xl font-display font-bold mt-2 inline-block px-3 py-1 rounded-lg text-white"
                    style={{ background: stepColor }}
                    data-testid="order-status"
                >{stepLabel}</h2>
                {riderStatus && (
                    <p
                        data-testid="rider-status"
                        className="mt-2 inline-block ml-2 align-middle text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-full text-white"
                        style={{ background: riderStatus.color }}
                    >{riderStatus.label}</p>
                )}
                {/* A Location Service Engine next-day / scheduled order shows its date + slot instead (below). */}
                {o.eta_minutes && !["NEXT_DAY", "SCHEDULED"].includes(o.delivery_plan?.mode) ? (() => {
                    // iter134q — respect tenant-level ETA display override.
                    // A pan-India tenant may hide this pill entirely or
                    // replace the "N mins" with a literal "2 Days".
                    const etaText = formatDeliveryEta(tenant, o.eta_minutes);
                    return etaText ? (
                        <p
                            data-testid="order-eta"
                            className="text-sm text-muted-foreground mt-2"
                        >ETA {etaText}</p>
                    ) : null;
                })() : null}
            </section>

            {status === "DELIVERED" && !reviewBlockedByReturn && (
                <section className="mx-4 mb-4">
                    <button
                        type="button"
                        data-testid="open-review"
                        onClick={() => setShowReview(true)}
                        className="w-full bg-amber-50 hover:bg-amber-100 dark:bg-amber-950/40 dark:hover:bg-amber-950/60 border border-amber-300 text-amber-900 dark:text-amber-200 rounded-2xl px-4 py-3 flex items-center gap-3"
                    >
                        <div className="w-10 h-10 rounded-full bg-amber-200 dark:bg-amber-800 inline-flex items-center justify-center">
                            <Star size={18} fill="currentColor" className="text-amber-600 dark:text-amber-300" />
                        </div>
                        <div className="flex-1 text-left">
                            <p className="font-bold text-sm">
                                {existingReview ? `You rated this ${existingReview.overall_rating}/5` : "Rate this order"}
                            </p>
                            <p className="text-xs opacity-80">
                                {existingReview ? "Tap to edit your review" : "Share your experience — takes 10 seconds"}
                            </p>
                        </div>
                        <span className="text-xs font-bold">{existingReview ? "Edit ›" : "Rate ›"}</span>
                    </button>
                </section>
            )}

            {/* Request Return — DELIVERED orders only, and only when the
                tenant (via Super Admin) has Returns enabled. Once a return
                request exists in ANY status — PENDING, APPROVED, or
                REJECTED — it's terminal: shows a status pill, never the
                button again. A rejection is a final admin decision, not
                an invitation to resubmit (matches the backend's dedup
                guard, which now blocks a new request in all three
                statuses). No request yet + within the window → button;
                no request yet + window closed → explanatory note. */}
            {status === "DELIVERED" && tenant?.returns_enabled && (
                <section className="mx-4 mb-4" data-testid="return-section">
                    {returnRequest ? (
                        <div
                            className="w-full rounded-2xl px-4 py-3 border"
                            data-testid="return-status-pill"
                            style={{
                                background: returnRequest.status === "APPROVED" ? "rgba(16,185,129,0.08)"
                                    : returnRequest.status === "REJECTED" ? "rgba(239,68,68,0.08)" : "rgba(245,158,11,0.08)",
                                borderColor: returnRequest.status === "APPROVED" ? "#10b981"
                                    : returnRequest.status === "REJECTED" ? "#ef4444" : "#f59e0b",
                            }}
                        >
                            <p className="font-bold text-sm">
                                {returnRequest.status === "APPROVED" && `Return approved — ₹${returnRequest.refund_amount} refunded to wallet`}
                                {returnRequest.status === "REJECTED" && "Return not approved"}
                                {returnRequest.status === "PENDING" && "Return requested — awaiting review"}
                            </p>
                            {returnRequest.status === "REJECTED" && returnRequest.reject_reason && (
                                <p className="text-xs text-muted-foreground mt-1">{returnRequest.reject_reason}</p>
                            )}
                        </div>
                    ) : returnWindowOpen ? (
                        <button
                            type="button"
                            data-testid="request-return-btn"
                            onClick={() => setShowReturnModal(true)}
                            className="w-full bg-muted hover:bg-muted/80 border border-border rounded-2xl px-4 py-3 flex items-center gap-3"
                        >
                            <div className="w-10 h-10 rounded-full bg-muted-foreground/10 inline-flex items-center justify-center">
                                <Undo2 size={18} className="text-muted-foreground" />
                            </div>
                            <div className="flex-1 text-left">
                                <p className="font-bold text-sm">Request a return</p>
                                <p className="text-xs text-muted-foreground">Item damaged, wrong, or missing?</p>
                            </div>
                            <span className="text-xs font-bold">Request ›</span>
                        </button>
                    ) : (
                        <div className="w-full rounded-2xl px-4 py-3 border border-border bg-muted/50" data-testid="return-window-closed">
                            <p className="text-xs text-muted-foreground">
                                The return window for this order ({returnWindowDays} day{returnWindowDays !== 1 ? "s" : ""} from delivery) has closed.
                            </p>
                        </div>
                    )}
                </section>
            )}

            {/* Cancel button — only available BEFORE the shop confirms.
                Once status flips to CONFIRMED+ the customer can't unilaterally
                cancel; they have to contact the shop or tenant admin. */}
            {status === "PLACED" && (
                <section className="mx-4 mb-4">
                    <button
                        type="button"
                        data-testid="cancel-order-btn"
                        onClick={cancelOrder}
                        disabled={cancelling}
                        className="w-full bg-red-50 hover:bg-red-100 dark:bg-red-950/40 dark:hover:bg-red-950/60 border border-red-200 text-red-900 dark:text-red-200 rounded-2xl px-4 py-3 inline-flex items-center justify-center gap-2 text-sm font-bold disabled:opacity-50"
                    >
                        {cancelling
                            ? <Loader2 size={14} className="animate-spin" />
                            : <X size={14} />}
                        Cancel this order
                    </button>
                    <p className="text-[11px] text-muted-foreground text-center mt-1.5">
                        You can cancel until the shop starts preparing. Refund posts to your wallet immediately.
                    </p>
                </section>
            )}

            {/* Location Service Engine tenants only (renders nothing otherwise). */}
            <LseOrderExtras className="m-4" orderId={o.id} status={o.status} riderStatus={o.rider_status} onLiveTick={loadTrack} />

            <OrderTrackMap track={track} points={points} hasMap={hasMap} />

            {track?.rider && (
                <ContactCard
                    testid="track-rider-card"
                    title="Rider"
                    icon={<Bike size={18} />}
                    name={track.rider.name}
                    sub={[track.rider.vehicle_type, track.rider.vehicle_number].filter(Boolean).join(" · ") || null}
                    phone={track.rider.mobile_number}
                />
            )}

            {track?.merchant_contact && (
                <ContactCard
                    testid="track-merchant-card"
                    title="Restaurant"
                    icon={<Store size={18} />}
                    name={track.shop?.name}
                    sub={null}
                    phone={track.merchant_contact.mobile_number}
                />
            )}

            <section className="m-4 bg-card rounded-2xl border border-border p-5" data-testid="track-timeline">
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Timeline</p>
                <ol className="mt-3 space-y-2">
                    {events.length === 0 && (
                        <li className="text-sm text-muted-foreground">No updates yet.</li>
                    )}
                    {events.map((ev, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm" data-testid={`tl-${ev.status}`}>
                            <span className="mt-1.5 w-2 h-2 rounded-full bg-primary shrink-0" />
                            <div className="flex-1">
                                <p className="font-semibold">{ev.status}</p>
                                {ev.message && <p className="text-xs text-muted-foreground">{ev.message}</p>}
                            </div>
                            <span className="text-[11px] text-muted-foreground shrink-0">{formatTs(ev.ts)}</span>
                        </li>
                    ))}
                </ol>
            </section>

            </div>
            <div className="min-w-0">
            {track?.support_contact && (
                <SupportCard support={track.support_contact} />
            )}

            <section className="m-4 bg-card rounded-2xl border border-border p-5 text-sm space-y-2" data-testid="track-summary">
                <h3 className="font-bold mb-2">Order summary</h3>
                {(o.items || []).map((it) => (
                    <div key={(it.product_id || "") + (it.variant_id || "") + it.name} className="flex items-center justify-between">
                        <span className="line-clamp-1">{it.name} × {it.quantity}</span>
                        <span>{inr2(it.line_total ?? ((it.selling_price || 0) * (it.quantity || 0)))}</span>
                    </div>
                ))}
                <div className="border-t border-border my-2"></div>
                <SummaryLine k="Subtotal" v={inr2(o.totals?.subtotal || 0)} />
                {(o.totals?.packaging_fee || 0) > 0 && (
                    <SummaryLine k="Packaging" v={inr2(o.totals.packaging_fee)} />
                )}
                <SummaryLine
                    k="Delivery fee"
                    v={(o.totals?.delivery_fee || 0) === 0
                        ? <span className="text-emerald-600 font-semibold">FREE</span>
                        : inr2(o.totals.delivery_fee)}
                />
                {o.totals?.delivery_distance_km != null && (o.totals?.delivery_fee || 0) > 0 && (
                    <p className="text-[11px] text-muted-foreground -mt-1" data-testid="track-fee-breakdown">
                        {o.totals.delivery_distance_km} km from shop
                        {o.totals.delivery_base_fee != null && o.totals.delivery_per_km_fee != null
                            ? ` · ₹${o.totals.delivery_base_fee} base + ₹${o.totals.delivery_per_km_fee}/km extra`
                            : ""}
                    </p>
                )}
                {(o.totals?.gst_and_other_charges || o.totals?.tax || 0) > 0 && (
                    <SummaryLine k="Taxes & charges" v={inr2(o.totals?.gst_and_other_charges || o.totals?.tax || 0)} />
                )}
                {(o.totals?.coupon_discount || 0) > 0 && (
                    <SummaryLine
                        k={<span className="text-emerald-700">Coupon ({o.totals.coupon_code || "applied"})</span>}
                        v={<span className="text-emerald-700 font-bold" data-testid="track-coupon-discount">−{inr2(o.totals.coupon_discount)}</span>}
                    />
                )}
                {(o.totals?.coins_discount || 0) > 0 && (
                    <SummaryLine
                        k={<span className="text-amber-700">Coins ({o.totals.coins_used})</span>}
                        v={<span className="text-amber-700 font-bold" data-testid="track-coins-discount">−{inr2(o.totals.coins_discount)}</span>}
                    />
                )}
                {(o.totals?.wallet_used || 0) > 0 && (
                    <SummaryLine
                        k={<span className="text-violet-700">Wallet used</span>}
                        v={<span className="text-violet-700">−{inr2(o.totals.wallet_used)}</span>}
                    />
                )}
                <div className="border-t border-border my-2"></div>
                <SummaryLine
                    k={<span className="font-bold">To Pay</span>}
                    v={<span className="font-bold" data-testid="track-grand-total">{inr2(o.totals?.grand_total || 0)}</span>}
                />
                {((o.totals?.coupon_discount || 0) + (o.totals?.coins_discount || 0)) > 0 && (
                    <p className="text-[11px] text-emerald-700 text-right font-semibold pt-0.5" data-testid="track-savings">
                        You saved {inr2((o.totals?.coupon_discount || 0) + (o.totals?.coins_discount || 0))} on this order
                    </p>
                )}
                <p className="text-xs text-muted-foreground pt-1">Payment: {o.payment_method} ({o.payment_status})</p>
            </section>

            {track?.customer?.address_line && (
                <section className="m-4 bg-card rounded-2xl border border-border p-5 text-sm" data-testid="track-address">
                    <p className="font-bold mb-1 inline-flex items-center gap-1.5"><MapPin size={14} /> Delivering to</p>
                    <p className="text-muted-foreground">{track.customer.address_line}</p>
                </section>
            )}
            </div>
            </div>

            {showReview && (
                <ReviewModal
                    orderId={id}
                    hasRider={!!o.rider_id}
                    onClose={() => setShowReview(false)}
                    onSubmitted={() => { setShowReview(false); loadReviewState(); }}
                />
            )}

            {showReturnModal && (
                <div
                    className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
                    onClick={() => !submittingReturn && setShowReturnModal(false)}
                >
                    <div
                        className="bg-card rounded-3xl p-6 w-full max-w-sm shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="return-request-modal"
                    >
                        <h2 className="text-lg font-bold mb-1">Request a return</h2>
                        <p className="text-xs text-muted-foreground mb-4">Tell us what went wrong — our team will review and, if approved, refund straight to your wallet.</p>
                        <label className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Reason</label>
                        <textarea
                            rows={3}
                            value={returnReason}
                            onChange={(e) => setReturnReason(e.target.value)}
                            placeholder="e.g. Item arrived damaged"
                            className="mt-1 w-full bg-muted rounded-xl px-3 py-2 text-sm outline-none"
                            data-testid="return-request-reason"
                        />
                        <div className="flex gap-2 mt-5">
                            <button
                                onClick={() => setShowReturnModal(false)}
                                disabled={submittingReturn}
                                className="flex-1 rounded-xl bg-muted py-2.5 text-sm font-semibold"
                                data-testid="return-request-cancel"
                            >Cancel</button>
                            <button
                                onClick={submitReturnRequest}
                                disabled={submittingReturn}
                                className="flex-1 rounded-xl bg-primary text-primary-foreground py-2.5 text-sm font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
                                data-testid="return-request-submit"
                            >{submittingReturn && <Loader2 size={13} className="animate-spin" />}Submit</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function ContactCard({ testid, title, icon, name, sub, phone }) {
    return (
        <section className="m-4 bg-card rounded-2xl border border-border p-4 flex items-center gap-3" data-testid={testid}>
            <div className="w-10 h-10 rounded-full bg-primary/10 text-primary inline-flex items-center justify-center">{icon}</div>
            <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{title}</p>
                <p className="font-semibold truncate">{name || "—"}</p>
                {sub && <p className="text-xs text-muted-foreground truncate">{sub}</p>}
            </div>
            {phone && (
                <CallButton
                    phone={phone}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-primary text-primary-foreground px-3 py-2 text-xs font-bold"
                    testid={`${testid}-call`}
                ><Phone size={14} /> Call</CallButton>
            )}
        </section>
    );
}

function SupportCard({ support }) {
    return (
        <section className="m-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-2xl p-4" data-testid="track-support-card">
            <p className="text-[10px] uppercase tracking-wider font-bold text-amber-700 dark:text-amber-300">Need help?</p>
            <p className="text-sm font-semibold mt-0.5">Contact customer support</p>
            <div className="mt-3 flex gap-2">
                {support.whatsapp && (
                    <a
                        href={support.whatsapp.deep_link}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-xl bg-green-600 text-white px-3 py-2 text-xs font-bold"
                        data-testid="track-support-whatsapp"
                    ><MessageCircle size={14} /> WhatsApp</a>
                )}
                {support.call && (
                    <a
                        href={support.call.deep_link}
                        className="inline-flex items-center gap-1.5 rounded-xl bg-foreground text-background px-3 py-2 text-xs font-bold"
                        data-testid="track-support-call"
                    ><Phone size={14} /> Call</a>
                )}
            </div>
        </section>
    );
}

function SummaryLine({ k, v }) {
    return (
        <div className="flex items-center justify-between">
            <span className="text-muted-foreground">{k}</span>
            <span>{v}</span>
        </div>
    );
}

function formatTs(ts) {
    if (!ts) return "";
    try { return new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
    catch { return ts; }
}
